import React from 'react';
import { format } from 'date-fns';
import isNil from 'lodash.isnil';
import isEmpty from 'lodash.isempty';
import {
    RESPONSE,
    CLASS_NAME,
    FORM,
    WEB_ENTRY_DATE,
    WEB_ENTRY_HEADER_TITLES,
    WEB_ENTRY_TYPE_OF_CELLS,
    WEB_ENTRY_FIELD_NAMES,
} from '../../constants/shared/WebEntryConstants';
import { getGradeTitle } from '../../constants/shared/GradesConstants';
import { validate } from './validation';
import { ERROR } from '../../constants/shared/CommonConstants';
import { TITLE } from '../../constants/shared/PMConstants';
import DateCell from './cells/DateCell';
import DropDownCell from './cells/DropDownCell';
import DynamicColumn from './cells/DynamicColumn';
import InputCell from './cells/InputCell';
import StudentNameCell from './cells/StudentNameCell';
import { generateReactKey } from '../../utils/shared/commonUtils';

const {
    STUDENT_NAME_FIELD,
    DATE_FIELD,
    FORM_GRADE_FIELD,
    FORM_NUMBER_FIELD,
    CORRECT_FIELD,
    INCORRECT_FIELD,
    DYNAMIC_FIELD,
} = WEB_ENTRY_FIELD_NAMES;

const { PREVENT_VALIDATION, LONG_CONTAINER } = CLASS_NAME;
const { CORRECT_RESPONSES, INCORRECT_RESPONSES } = RESPONSE;
const { DATE: DATE_FIELD_KEY } = WEB_ENTRY_DATE;
const { FORM_NUMBER } = FORM;
const { PM_TITLE } = TITLE;
const { DATE, GRADE, FORM_NUMBER: FORM_NUMBER_TITLE, CORRECT, INCORRECT } = WEB_ENTRY_HEADER_TITLES;
const { BENCHMARK_CELLS, PROGRESS_CELLS } = WEB_ENTRY_TYPE_OF_CELLS;

export const getDateString = date => (date ? format(date, 'MM/dd/yy') : '- -/- -/- -');

export const getDate = (dateValue, selectedDate) =>
    selectedDate || (dateValue ? new Date(dateValue) : null);

export const isEntering = (
    error,
    isPmPanelType,
    { correctResponses, incorrectResponses, formValues },
) => {
    const isBenchmarkEnteringStarted =
        error || !isNil(correctResponses) || !isNil(incorrectResponses);

    return isPmPanelType
        ? isBenchmarkEnteringStarted || !isEmpty(formValues)
        : isBenchmarkEnteringStarted;
};

export const getSpecificDataByPanelType = (panelType, mainTitle, isSecurePeriod) => {
    return panelType === PM_TITLE
        ? {
              assessmentType: 'Progress Monitoring',
              headerTitle: `${mainTitle} Progress Monitoring Results`,
              panelClassName: 'progress-view',
              typeOfCells: PROGRESS_CELLS,
              getErrors: ({ correctResponses, incorrectResponses, date, formValues }) => {
                  const fields = {
                      [CORRECT_RESPONSES]: correctResponses,
                      [INCORRECT_RESPONSES]: incorrectResponses,
                      [FORM_NUMBER]: formValues.sequence,
                      [DATE_FIELD_KEY]: date,
                  };

                  return {
                      fields,
                      errorObject: validate(fields, formValues.questionsCount, true, mainTitle),
                  };
              },
              headerCells: [
                  {
                      cellClassName: LONG_CONTAINER,
                      title: DATE,
                      iconClassName: 'icon-amplify-date',
                  },
                  {
                      cellClassName: LONG_CONTAINER,
                      title: GRADE,
                      iconClassName: 'form-grade-icon',
                      additionalElement: <div className="plus-sign">+</div>,
                  },
                  {
                      cellClassName: LONG_CONTAINER,
                      title: FORM_NUMBER_TITLE,
                      iconClassName: 'form-number-icon',
                  },
                  {
                      cellClassName: '',
                      title: CORRECT,
                      iconClassName: 'correct-icon',
                  },
                  {
                      cellClassName: '',
                      title: INCORRECT,
                      iconClassName: 'icon-amplify-cancel',
                  },
              ],
          }
        : {
              assessmentType: 'Benchmark',
              headerTitle: `${mainTitle} Benchmark Results`,
              isSecureHeaderText: isSecurePeriod,
              panelClassName: '',
              typeOfCells: BENCHMARK_CELLS,
              getErrors: ({ correctResponses, incorrectResponses, date, formValues }) => {
                  const fields = {
                      [CORRECT_RESPONSES]: correctResponses,
                      [INCORRECT_RESPONSES]: incorrectResponses,
                      [DATE_FIELD_KEY]: date,
                  };

                  return {
                      fields,
                      errorObject: validate(fields, formValues.questionsCount, false, mainTitle),
                  };
              },
              headerCells: [
                  {
                      cellClassName: LONG_CONTAINER,
                      title: DATE,
                      iconClassName: 'icon-amplify-date',
                  },

                  {
                      cellClassName: '',
                      title: CORRECT,
                      iconClassName: 'correct-icon',
                  },
                  {
                      cellClassName: '',
                      title: INCORRECT,
                      iconClassName: 'icon-amplify-cancel',
                  },
              ],
          };
};

export const getCellsData = ({
    grade,
    studentInfo,
    result,
    error,
    handlers,
    elementsRef,
    rowData,
    rowConfig,
    setIsRowFocused,
    setAlertType,
    isWebEntryGrade,
    formIdsData,
    formGradeDropdownItems,
    webEntryClassName,
}) => {
    const { date, correctResponses, incorrectResponses, formGrade, formValues } = rowData;
    const {
        handleNumberInputChange,
        handleDateValueChange,
        handleKeyDown,
        setFormGradeHandler,
        setFormValuesHandler,
    } = handlers;

    const { isRowDisabled, isRowSaving, isRowFocused, isAlreadyCreated } = rowConfig;
    const { invalidateButtonRef, datePickerRef } = elementsRef;
    const isNotEditable = !isWebEntryGrade || isAlreadyCreated || isRowSaving;
    const dateString = getDateString(date);
    const formGradeChosemItem = {
        uid: formGrade || grade,
        name: getGradeTitle(formGrade || grade),
    };
    const formNumberChosenItem = isEmpty(formValues)
        ? {}
        : {
              uid: formValues.formId,
              name: `PM-${formValues.sequence}`,
          };

    const formNumberItems = formIdsData
        .filter(item => item.grade === (formGrade || grade))
        .sort((item, nextItem) => item.sequence - nextItem.sequence)
        .map(formItem => ({
            uid: formItem.formId,
            name: `PM-${formItem.sequence}`,
        }));
    const isCheckBoxEnabled = isNil(rowData.correctResponses) || isNil(rowData.incorrectResponses);

    const studentCellData = {
        cellType: 'studName',
        ...studentInfo,
    };

    const dateCellData = {
        cellType: 'date',
        dateString: isWebEntryGrade ? dateString : 'N/A',
        dateStringClassName: LONG_CONTAINER,
        isNotEditable,
        isRowDisabled,
        datePicker: {
            date,
            datePickerRef,
            error,
            showAllStudentsCheckbox: isCheckBoxEnabled,
            className: LONG_CONTAINER,
            setIsRowFocused,
            isRowFocused,
            handleDateValueChange,
            handleKeyDown,
        },
    };

    const formGradeCellData = {
        cellType: 'formGrade',
        dataString: isWebEntryGrade ? formGrade || grade : 'N/A',
        dataStringClassName: LONG_CONTAINER,
        isNotEditable,
        dropDown: {
            error,
            dropDownItemClassNames: `${PREVENT_VALIDATION}`,
            dropDownClassNames: 'form-grade-dropdown',
            buttonClassNames: `${PREVENT_VALIDATION}`,
            onDropDownItemClick: setFormGradeHandler,
            chosenItem: formGradeChosemItem,
            items: formGradeDropdownItems,
            isDisabled: isRowDisabled,
        },
    };

    const formNumberCellData = {
        cellType: 'formNumber',
        dataString: isWebEntryGrade ? formValues?.sequence : 'N/A',
        dataStringClassName: LONG_CONTAINER,
        isNotEditable,
        dropDown: {
            error,
            onDropDownItemClick: setFormValuesHandler,
            dropDownClassNames: 'form-number-dropdown',
            chosenItem: formNumberChosenItem,
            items: formNumberItems,
            isDisabled: isEmpty(formIdsData) || isRowDisabled,
            dropDownItemClassNames: `${PREVENT_VALIDATION}`,
            buttonClassNames: `${PREVENT_VALIDATION} ${
                error && error.highlightedInputs.find(inputName => inputName === FORM_NUMBER)
                    ? ERROR
                    : ''
            }`,
        },
    };

    const correctCellData = {
        cellType: 'correct',
        dataString: isWebEntryGrade ? correctResponses : 'N/A',
        dataStringClassName: '',
        isNotEditable,
        input: {
            isRowDisabled,
            error,
            handleChange: e => handleNumberInputChange(e, CORRECT_RESPONSES),
            name: CORRECT_RESPONSES,
            inputClassName: CLASS_NAME.CORRECT_RESPONSES,
            className: '',
        },
    };

    const incorrectCellData = {
        cellType: 'incorrect',
        dataString: isWebEntryGrade ? incorrectResponses : 'N/A',
        dataStringClassName: '',
        isNotEditable,
        input: {
            isRowDisabled,
            error,
            handleChange: e => handleNumberInputChange(e, INCORRECT_RESPONSES),
            handleKeyDown,
            name: INCORRECT_RESPONSES,
            inputClassName: CLASS_NAME.INCORRECT_RESPONSES,
            className: '',
        },
    };

    const dynamicCellData = {
        cellType: 'dynamic',
        result,
        showSavedLabel: isRowSaving,
        invalidateButtonRef,
        setAlertType,
        error,
        isNotEditable,
        isWebEntryGrade,
        webEntryClassName,
    };

    return {
        [BENCHMARK_CELLS]: [
            studentCellData,
            dateCellData,
            correctCellData,
            incorrectCellData,
            dynamicCellData,
        ],
        [PROGRESS_CELLS]: [
            studentCellData,
            dateCellData,
            formGradeCellData,
            formNumberCellData,
            correctCellData,
            incorrectCellData,
            dynamicCellData,
        ],
    };
};

const fieldsMapperByType = (cellData, elementKey, isSecurePeriod) => ({
    [STUDENT_NAME_FIELD]: <StudentNameCell key={elementKey} {...cellData} />,
    [DATE_FIELD]: <DateCell key={elementKey} {...cellData} />,
    [FORM_GRADE_FIELD]: <DropDownCell key={elementKey} {...cellData} />,
    [FORM_NUMBER_FIELD]: <DropDownCell key={elementKey} {...cellData} />,
    [CORRECT_FIELD]: <InputCell key={elementKey} {...cellData} />,
    [INCORRECT_FIELD]: <InputCell key={elementKey} {...cellData} />,
    [DYNAMIC_FIELD]: (
        <DynamicColumn key={elementKey} isSecurePeriod={isSecurePeriod} {...cellData} />
    ),
});

export const getFieldByType = (cellData, index, studentInfo, isSecurePeriod) => {
    const { cellType } = cellData;
    const elementKey = generateReactKey(
        index,
        `${studentInfo.firstName}-${studentInfo.lastName}-${cellType}-cell`,
    );
    return fieldsMapperByType(cellData, elementKey, isSecurePeriod)[cellType] || <></>;
};

export const updateRowStatus = (rowsStatuses, studentId, status) => ({
    ...rowsStatuses,
    [studentId]: status,
});
